import lab_icon from './assets/lab_icon.png';
import foundry_icon from './assets/foundry_icon.png';
import bridge_icon from './assets/bridge_icon.png';
import venture_icon from './assets/venture_icon.png';
import { SubmissionPublicFieldFragment } from '@/graphql/fragment/submission.public.fragment';

// enums
export enum Models {
  LAB = 'LAB',
  FOUNDRY = 'FOUNDRY',
  BRIDGE = 'BRIDGE',
  VENTURE = 'VENTURE',
}

// consts
export const GUIDANCE_TEXT = 'Select one answer';
export const BUTTON_TEXT = 'continue';
export const QUESTION = 'Question';
export const RESULTS = 'Results';
export const ARTICLE_TEXT =
  'Curious how leading organizations are rethinking innovation in today’s fast-changing world? Check out the full article here to learn how four distinct innovation models—Lab, Foundry, Bridge, and Venture—can help your organization stay ahead, adapt to disruption, and become “Innovation Ready.”';
export const ARTICLE_LINK_TEXT = 'Read the Article Here';
export const LOADING_TEXT = 'Please wait... Your results are being calculated.';
export const NEXT_STEPS = 'Next Steps:';
export const START_LEFT_CONTEXT =
  'Innovation has transformed dramatically over the past five years—shaped by generative AI, economic changes, shifting consumer expectations, and the lasting effects of COVID-19. As priorities and pressures continue to evolve, defining what it means to be “innovation ready” can be challenging.';
export const START_RIGHT_CONTEXT =
  'We’ve identified four innovation models tailored to today’s environment. Take our brief, 5-question quiz to discover which models can help your organization strengthen innovation readiness and resilience.';
export const START_INNOVATION_MODEL =
  '*An innovation model is the framework that connects your strategy, resources, and processes to achieve your innovation ambition.';
export const START_LEARN_MORE = 'Learn more about Innovation Models here.';
export const HIGHEST_PRIORITY = 'HIGHEST PRIORITY';
export const LOWEST_PRIORITY = 'LOWEST PRIORITY';
export const CLICK_AND_DRAG = 'Click and drag in order to rank.';
export const OPTIONS_THAT_DO_NOT_APPLY =
  'Options that do not apply do not have to be ranked.';
export const DATA_PERMISSION_ID = 'J7vPOV';
export const TERMS_ID = 'Y1nq2V';

export const innovationCultureOptionCards = [
  {
    header: 'LOW',
    value: 'BRIDGE',
    items: [
      'Innovation is not a strategic priority; resources and executive involvement are minimal.',
      'Few or no processes or structures support innovation.',
      'Efforts are sporadic and not integrated into business strategy.',
    ],
  },
  {
    header: 'MEDIUM',
    value: 'LAB,FOUNDRY',
    items: [
      'Some processes and frameworks are in place, with increasing resources.',
      'Leadership moderately supports a culture of experimentation and learning.',
      'Innovation is valued but remains somewhat separate from core strategy.',
    ],
  },
  {
    header: 'HIGH',
    value: 'VENTURE',
    items: [
      'Innovation is a key driver of business strategy, supported by company culture and well-defined processes.',
      'Leadership actively champions and integrates innovation into organizational goals.',
      'Strong commitment to investing in new technologies, with innovation embedded at all levels.',
    ],
  },
];

export const arcColors = {
  [Models.LAB]: '#C4D600', // LAB
  [Models.FOUNDRY]: '#6FC2B4', // FOUNDRY
  [Models.BRIDGE]: '#62B5E5', // BRIDGE
  [Models.VENTURE]: '#005587', // VENTURE
};

// helper functions
export function getModelTooltipData(model: string) {
  switch (model.toLowerCase()) {
    case 'lab':
      return {
        icon: lab_icon,
        color: '#C4D600',
        title: 'The Lab model',
        subtitle:
          'helps you build innovation skills and creative problem-solving capabilities, enabling rapid improvements to your core offerings.',
        items: [
          'Focus on creating space for experimentation and celebrating creative solutions.',
          'Consider launching innovation labs, workshops, or training programs to start building momentum.',
        ]
      };
    case 'foundry':
      return {
        icon: foundry_icon,
        color: '#6FC2B4',
        title: 'The Foundry model',
        subtitle:
          'enables quick prototyping, testing, and launching of new concepts—turning bold ideas into real, de-risked opportunities.',
        items: [
          'Prioritize rapid experimentation and iterative development to bring new ideas to life.',
          'Set up dedicated teams or spaces for prototyping and testing.',
        ],
      };
    case 'bridge':
      return {
        icon: bridge_icon,
        color: '#62B5E5',
        title: 'The Bridge model',
        subtitle:
          'boosts your innovation efforts through strategic partnerships, giving you access to the skills and resources you need—right when you need them.',
        items: [
          'Identify partnership opportunities that can complement your strengths and fill capability gaps.',
          'Establish clear collaboration goals and shared success metrics.',
        ],
        
      };
    case 'venture':
      return {
        icon: venture_icon,
        color: '#005587',
        title: 'The Venture model',
        subtitle:
          'is all about scouting and acquiring innovative startups or assets to strengthen your portfolio and bring in fresh ideas and capabilities.',
        items: [
          'Identify emerging startups or technologies that align with your strategic goals.',
          'Develop a process for evaluating, investing in, or acquiring promising ventures.',
        ]
      };
    default:
      return {
        icon: lab_icon,
        color: '#C4D600',
        title: model.toUpperCase(),
        subtitle: '',
        items: []
      };
  }
}

function removeQuotes(value: string): string {
  return value.replace(/"/g, '');
}

function repeatElementsByIndex(splitValues: string[]): string[] {
  const arrLength = splitValues.length;
  const newArray: string[] = [];

  splitValues.forEach((element, index) => {
    const repeatCount = arrLength - index;
    for (let i = 0; i < repeatCount; i++) {
      newArray.push(removeQuotes(element.trim()));
    }
  });
  return newArray;
}

export const DRAG_AND_DROP_FIELD_IDS = ['w1ePZV', 'M1D9b7', 'YjAvMV'];
export const CUSTOM_RADIO_FIELD_ID = 'Bjae6j';

export function getValues(answers: SubmissionPublicFieldFragment[]): string[] {
  const values: string[] = [];
  answers.forEach((answer) => {
    // Drag and drop fields: repeat elements by index
    if (DRAG_AND_DROP_FIELD_IDS.includes(answer.field.id)) {
      const splitValues = answer.value.split(',');
      const repeatedValues = repeatElementsByIndex(splitValues);
      repeatedValues.forEach((val) => values.push(val));
      return;
    }

    // Comma-separated values with quotes
    if (answer.field.id === CUSTOM_RADIO_FIELD_ID) {
      answer.value
        .split(',')
        .map((val) => removeQuotes(val.trim()))
        .forEach((val) => values.push(val));
      return;
    }

    // Default: just remove quotes and push
    values.push(removeQuotes(answer.value));
  });
  return values;
}

export function calculateModelPercentages(
  values: string[]
): Record<string, number> {
  const modelsScoreMap = {
    [Models.LAB]: 0,
    [Models.FOUNDRY]: 0,
    [Models.BRIDGE]: 0,
    [Models.VENTURE]: 0,
  };

  // Count each model occurrence
  for (const value of values) {
    if (modelsScoreMap.hasOwnProperty(value)) {
      modelsScoreMap[value as Models]++;
    }
  }

  // Calculate total valid values
  const total = Object.values(modelsScoreMap).reduce(
    (sum, count) => sum + count,
    0
  );

  // Calculate percentages for each model
  const percentages: Record<string, number> = {};
  Object.entries(modelsScoreMap).forEach(([model, count]) => {
    percentages[model] = total > 0 ? Math.round((count / total) * 100) : 0;
  });

  // Adjust for rounding so total is exactly 100%
  const sumPercent = Object.values(percentages).reduce((a, b) => a + b, 0);
  if (sumPercent !== 100) {
    // Find the model with the highest count and adjust its percentage
    const maxModel = Object.keys(percentages).reduce((a, b) =>
      percentages[a] > percentages[b] ? a : b
    );
    percentages[maxModel] += 100 - sumPercent;
  }

  return percentages;
}

export function getDominantModel(
  percentages: Record<string, number>
): string | null {
  for (const [model, percent] of Object.entries(percentages)) {
    if (percent >= 60) {
      return model;
    }
  }
  return null;
}

export function getDominantModelText(model: string | null): {
  title: string;
  context_one: string;
  context_two: string;
} {
  switch (model) {
    case 'LAB':
      return {
        title:
          'It sounds like you need to explore expanding your internal innovation capabilities!',
        context_one: 'The Lab model',
        context_two:
          'empowers your firm to innovate your core business by leveraging the unique skills of your teams--but don’t stop there! Click on each model below to explore how you can complement your lab with other innovation models.',
      };
    case 'FOUNDRY':
      return {
        title:
          'It sounds like you want to make bold moves to transform your sector!',
        context_one: 'The Foundry model',
        context_two:
          'nurtures transformational innovation to turn bold ideas into viable, de-risked offerings--but don’t stop there! Click on each model below to explore how you can complement your foundry with other innovation models.',
      };
    case 'BRIDGE':
      return {
        title:
          'It sounds like you have pressure to innovate fast but lack the necessary resources and skillsets...',
        context_one: 'The Bridge model',
        context_two:
          'provides access to leading market resources and skillsets to accelerate your time to market--but don’t stop there! Click on each model below to explore how you can complement your bridge with other innovation models.',
      };
    case 'VENTURE':
      return {
        title:
          'It sounds like you’re venturing into new areas but need to acquire external assets to make it happen...',
        context_one: 'The Venture model',
        context_two:
          'enables rapid expansion by acquiring new assets from external companies--but don’t stop there! Click on each model below to explore how you can complement your venture with other innovation models.',
      };
    default:
      return {
        title:
          'It sounds like there are multiple innovation models that you should explore...',
        context_one:
          'You may be at an early stage of defining your innovation ambition-- or you’re open to multiple paths forward.',
        context_two:
          'Click on each model below to discover how they might also support your innovation journey.',
      };
  }
}

export const getDraggableItemsById = (fieldId: string) => {
  switch (fieldId) {
    // que: What is your innovation ambition?
    case 'w1ePZV':
      return [
        {
          id: 'LAB',
          title: 'Enhance Core Offerings & Capabilities',
          content:
            'Focus on improving your current products, services, or processes to boost internal efficiency and/or enhance customer experience.',
        },
        {
          id: 'FOUNDRY',
          title: 'Build New Offerings & Assets',
          content:
            'Create something new for your organization or industry, testing uncertainties to determine if it will deliver value to customers and your business.',
        },
        {
          id: 'BRIDGE',
          title: 'Augment Resources',
          content:
            'Seek immediate impact by partnering to fill current gaps in skills, technology, or capabilities—without the time required for internal development.',
        },
        {
          id: 'VENTURE',
          title: 'Acquire New Capabilities',
          content:
            'Expand your organization’s reach by investing in startups or fostering internal entrepreneurial initiatives.',
        },
      ];
    case 'M1D9b7':
      // que: What is your biggest challenge in resourcing this effort from a talent (people) perspective?
      return [
        {
          id: 'LAB',
          title: 'Gaps in Critical Skillsets',
          content:
            'Our team lacks key skills or expertise needed for innovation (e.g., design thinking, technical skills, data analysis, or customer insights).',
        },
        {
          id: 'FOUNDRY',
          title: 'Low Engagement or Motivation',
          content:
            ' Team members are not energized, motivated, or fully committed to driving innovation, which impacts momentum and progress.',
        },
        {
          id: 'BRIDGE',
          title: 'Insufficient Headcount or Bandwidth',
          content:
            'We have too few people available, or our current staff are stretched too thin to dedicate meaningful time to innovation efforts.',
        },
        {
          id: 'VENTURE',
          title: 'Limited Industry or Domain Experience',
          content:
            'We do not have enough team members with deep experience or understanding of the relevant industry, market, or emerging trends.',
        },
      ];
    case 'YjAvMV':
      // que: Which of these strategies feel aligned with what your organization needs now to drive innovation forward?
      return [
        {
          id: 'LAB',
          title: 'Building Internal Innovation Capabilities',
          content:
            'Invest in developing internal processes, governance, training, and playbooks to strengthen innovation from within.',
        },
        {
          id: 'FOUNDRY',
          title: 'Accelerating Experimentation',
          content:
            'Prioritize rapid prototyping, pilot programs, and in-market testing to quickly validate new ideas and solutions.',
        },
        {
          id: 'BRIDGE',
          title: 'Expanding Talent Resources',
          content:
            'Bring in external experts, partners, or temporary staff to supplement internal teams and address capability gaps.',
        },
        {
          id: 'VENTURE',
          title: 'Fostering Entrepreneurial Mindsets',
          content:
            'Encourage entrepreneurial thinking and risk-taking, whether through internal ventures, incubators, or partnerships with startups.',
        },
      ];
    default:
      return [];
  }
};
